import React, { useEffect, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    Paper,
    Stack,
    Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PdfSourcePage from './PdfSourcePage'
import { formatSourceAccessDate } from './QualityReviewSourceAccess'

export default function InlineSourceViewer({
    open,
    source,
    onClose,
    onDownload,
    downloading = false
}) {
    const canPreview = Boolean(source?.file && source?.isPdf)
    const [previewStatus, setPreviewStatus] = useState(null)

    useEffect(() => {
        setPreviewStatus(null)
    }, [source])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            fullScreen={false}
            PaperProps={{
                sx: {
                    height: { xs: '96vh', md: '92vh' },
                    maxHeight: { xs: '96vh', md: '92vh' },
                    borderRadius: { xs: 2, md: 3 },
                    overflow: 'hidden',
                    '& .MuiTypography-body2': { fontSize: '0.98rem', lineHeight: 1.55 },
                    '& .MuiTypography-caption': { fontSize: '0.88rem', lineHeight: 1.45 },
                    '& .MuiTypography-overline': { fontSize: '0.88rem', lineHeight: 1.5 },
                    '& .MuiButton-root': { fontSize: '0.9rem' }
                }
            }}
        >
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.25}
                    justifyContent="space-between"
                    alignItems={{ md: 'center' }}
                    sx={{ px: { xs: 2, md: 2.5 }, py: 1.75, borderBottom: '1px solid #dce6eb' }}
                >
                    <Box sx={{ minWidth: 0, pr: { md: 2 } }}>
                        <Typography variant="overline" sx={{ color: '#557184', fontWeight: 850, letterSpacing: 1 }}>
                            Source document
                        </Typography>
                        <Typography sx={{ fontWeight: 900, color: '#173042', wordBreak: 'break-word' }}>
                            {source?.fileName || 'Source document'}
                        </Typography>
                        {source?.location && (
                            <Typography variant="body2" sx={{ color: '#3e5b6d', mt: 0.35, fontWeight: 700 }}>
                                Go to {source.location}
                            </Typography>
                        )}
                        <SourcePreviewGuidance source={source} previewStatus={previewStatus} />
                        {source?.accessExpiresAt && (
                            <Typography variant="caption" sx={{ display: 'block', color: '#526b7b', mt: 0.35, fontWeight: 700 }}>
                                Available until {formatSourceAccessDate(source.accessExpiresAt)} · Restricted to authorised reviewers.
                            </Typography>
                        )}
                    </Box>

                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        {source?.fullDocumentUrl && source?.isPdf && (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<OpenInNewIcon />}
                                component="a"
                                href={source.fullDocumentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Open full document
                            </Button>
                        )}
                        {source?.fullDocumentUrl && (
                            <Button
                                size="small"
                                variant={source?.isPdf ? 'text' : 'contained'}
                                startIcon={<DownloadIcon />}
                                onClick={() => onDownload?.(source)}
                                disabled={downloading}
                            >
                                {downloading ? 'Downloading…' : 'Download file'}
                            </Button>
                        )}
                        <IconButton aria-label="Close source viewer" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                <Box sx={{ flex: 1, minHeight: 0, p: { xs: 1, md: 1.5 }, backgroundColor: '#eef3f6' }}>
                    {canPreview ? (
                        <PdfSourcePage source={source} onStatusChange={setPreviewStatus} />
                    ) : (
                        <Paper sx={{ maxWidth: 720, mx: 'auto', mt: { xs: 2, md: 6 }, p: { xs: 2, md: 3 }, borderRadius: 3 }}>
                            <Alert severity="info" sx={{ borderRadius: 2.5 }}>
                                Download this file, open it in Word or Excel, then go to {source?.location || 'the location shown above'}.
                            </Alert>
                        </Paper>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    )
}

function SourcePreviewGuidance({ source, previewStatus }) {
    if (source && !source.isPdf) return null
    let message = 'The referenced page is shown below. Use Open full document to view every page.'
    if (previewStatus?.matchStatus === 'exact') {
        message = 'The text linked to this finding is highlighted. The highlight identifies the referenced location; it does not mean every highlighted word is incorrect. Use Open full document to view every page.'
    } else if (previewStatus?.matchStatus === 'normalized') {
        message = 'The unique line linked to this finding is highlighted. The highlight identifies the referenced location; it does not mean every highlighted word is incorrect. Use Open full document to view every page.'
    } else if (previewStatus?.matchStatus === 'ambiguous') {
        message = 'Several matching text instances were found, so Bubbly opened the referenced page without a potentially misleading highlight.'
    } else if (previewStatus?.matchStatus === 'not_found') {
        message = 'Bubbly opened the referenced page, but no unique exact text match was found, so no highlight is shown.'
    } else if (previewStatus?.matchStatus === 'too_complex') {
        message = 'This page contains too much extracted text for safe evidence matching, so Bubbly opened it without a highlight.'
    } else if (previewStatus?.matchStatus === 'page_only' || !(source?.highlightTexts || []).length) {
        message = 'The referenced page is shown without unrelated PDF annotations. Use Open full document to view every page.'
    }

    return (
        <Typography variant="caption" sx={{ display: 'block', color: '#6d7f8b', mt: 0.35 }}>
            {message}
            {previewStatus?.pageNumber
                ? ` PDF page ${previewStatus.pageNumber} of ${previewStatus.totalPages}${previewStatus.documentPageLabel ? `; the document footer shows page ${previewStatus.documentPageLabel}` : ''}.`
                : ''}
        </Typography>
    )
}
